import type { ComboBoxFilterChangeEvent } from "@tql/ui-components-react/kendo/dropdowns";
import type { TextAreaChangeEvent } from "@tql/ui-components-react/kendo/inputs";
import { Input, TextArea } from "@tql/ui-components-react/kendo/inputs";
import { Hint } from "@tql/ui-components-react/kendo/labels";
import { Card } from "@tql/ui-components-react/kendo/layout";
import { Button } from "@tql/ui-components-react/tql/buttons";
import { DatePicker } from "@tql/ui-components-react/tql/dateinputs";
import { ComboBox } from "@tql/ui-components-react/tql/dropdowns";
import { FormField } from "@tql/ui-components-react/tql/form/beta";
import {
	copy_02,
	dotsGrid,
	trash_01,
} from "@tql/ui-components-react/untitled/icons";
import { useCallback, useContext, useState } from "react";
import { useDebounceCallback } from "usehooks-ts";

import { validateStopDateAgainstPreviousAndNext } from "~/features/ftl/ftl-quotes/create-quote/components/quote-details-step/sortable-item-ui/ftl-stop/ftl-stop.tsx";
import { StopTypeSelector } from "~/features/ftl/ftl-quotes/create-quote/components/stop-type-selector/stop-type-selector.tsx";
import { useMinStopDate } from "~/features/ftl/ftl-quotes/hooks/use-after-2pm-est.tsx";
import { useLocationSearch } from "~/features/ftl/ftl-quotes/hooks/use-location-search.tsx";
import type { Stop } from "~/features/ftl/shared/ftl-stop";

import type { StopSortableProps } from "../../../create-ftl-shipment";
import { sortableItemUIContext } from "../../../create-ftl-shipment.tsx";
import {
	APPOINTMENT_INFORMATION_MAX_LENGTH,
	appointmentInformationValidator,
	shipperNameValidator,
	stopNumberValidator,
	streetAddressValidator,
} from "../../../create-ftl-shipment-validators.ts";
import * as styles from "./ftl-shipment-stop-form.module.css";

export type StopProps = {
	stop: Stop;
};

const locationValidator = (value: string) =>
	value ? "" : "Location is required.";

const stopTypeValidator = (value: string) =>
	value ? "" : "Stop Type is required.";

export function FTLShipmentStopForm(props: Readonly<StopProps>) {
	const stopsContext = useContext<StopSortableProps>(sortableItemUIContext);
	const minStopDate = useMinStopDate();
	const isDropStopType = props.stop.stopType === "Delivery";
	const [appointmentInformation, setAppointmentInformation] = useState(
		props.stop.appointmentInformation ?? "",
	);

	const stops = stopsContext?.stops ?? [];

	const onDeleteStop = (index: number) => {
		stopsContext?.onRemove(index);
	};

	const onCopyStop = (index: number) => {
		const selectedLocation = props.stop.location;
		const currentOptions = locationsData ?? [];
		const alreadyIncluded = currentOptions.some(
			(l) => l.location === selectedLocation?.location,
		);
		const locationOptions =
			selectedLocation && !alreadyIncluded
				? [selectedLocation, ...currentOptions]
				: locationsData;
		stopsContext?.onCopy(index, locationOptions);
	};

	const stopNumberLabel = isDropStopType ? "Receiver Number" : "Pickup Number";
	const shipperNameLabel = isDropStopType ? "Receiver Name" : "Shipper Name";

	const stopNumberFieldValidator = useCallback(
		(value: string) => stopNumberValidator(value, stopNumberLabel),
		[stopNumberLabel],
	);

	const shipperNameFieldValidator = useCallback(
		(value: string) => shipperNameValidator(value, shipperNameLabel),
		[shipperNameLabel],
	);

	const name = `stops[${stopsContext?.stops.findIndex((stop) => stop.id === props.stop.id)}]`;

	const stopIndex = stopsContext?.stops.findIndex(
		(stop) => stop.id === props.stop.id,
	);
	const { search, isPending, locationsData } = useLocationSearch(
		props.stop.locationOptions,
	);

	const locationSearch = useDebounceCallback(search, 300);

	const handleFilterChange = useCallback(
		(event: ComboBoxFilterChangeEvent) => {
			if ((event.filter.value as string).length > 2) {
				locationSearch(event.filter.value as string);
			}
		},
		[locationSearch],
	);

	const handleAppointmentInformationChange = (event: TextAreaChangeEvent) => {
		setAppointmentInformation(event.value ?? "");
	};

	const previousStopDate = stops[stopIndex - 1]?.stopDate ?? null;
	const nextStopDate = stops[stopIndex + 1]?.stopDate ?? null;
	const stopDateFieldValidator = useCallback(
		(value: Date | null) =>
			validateStopDateAgainstPreviousAndNext(
				value,
				minStopDate,
				previousStopDate,
				nextStopDate,
				stopIndex,
			),
		[minStopDate, previousStopDate, nextStopDate, stopIndex],
	);

	return (
		<Card>
			<div
				className={styles.stopContainer}
				data-testid={`ftl-shipment-stop-${stopIndex}`}
			>
				<div className={styles.stopControls}>
					<div className={styles.stopFields}>
						<div className={styles.stopPrimaryFields}>
							<div className={styles.stopTypeControl}>
								<FormField
									required
									className={"k-mt-0"}
									component={StopTypeSelector}
									label={"Stop Type"}
									name={`${name}.stopType`}
									stop={props.stop}
									stopIndex={stopIndex}
									validator={stopTypeValidator}
								/>
							</div>

							<div
								className={styles.stopDateControl}
								data-testid={`ftl-shipment-stop-date-${stopIndex}`}
							>
								<FormField
									required
									component={DatePicker}
									defaultValue={minStopDate}
									label={"Stop Date"}
									min={minStopDate}
									name={`${name}.stopDate`}
									validator={stopDateFieldValidator}
								/>
							</div>
							<div className={styles.stopNumberControl}>
								<FormField
									component={Input}
									label={stopNumberLabel}
									name={`${name}.stopNumber`}
									validator={stopNumberFieldValidator}
								/>
							</div>
						</div>
						<div className={styles.stopSecondaryFields}>
							<div
								className={styles.cityStateControl}
								data-testid={`stop-location-${stopIndex}`}
							>
								<FormField
									filterable
									required
									component={ComboBox}
									data={locationsData ?? undefined}
									dataItemKey={"location"}
									label={"City, State/Province or Postal Code"}
									loading={isPending}
									name={`${name}.location`}
									onFilterChange={handleFilterChange}
									placeholder={"Search by city name or postal code."}
									textField={"location"}
									validator={locationValidator}
								/>
							</div>
							<div className={styles.shipperNameControl}>
								<FormField
									required
									component={Input}
									label={shipperNameLabel}
									name={`${name}.shipperName`}
									validator={shipperNameFieldValidator}
								/>
							</div>
							<div className={styles.streetAddressControl}>
								<FormField
									required
									component={Input}
									label={"Street Address"}
									name={`${name}.streetAddress`}
									validator={streetAddressValidator}
								/>
							</div>
							<div className={styles.appointmentInfoControl}>
								<FormField
									component={TextArea}
									label={"Appointment Information"}
									name={`${name}.appointmentInformation`}
									onChange={handleAppointmentInformationChange}
									resizable={"none"}
									rows={3}
									validator={appointmentInformationValidator}
									value={appointmentInformation}
								/>
								<Hint direction={"end"}>
									{appointmentInformation.length}/
									{APPOINTMENT_INFORMATION_MAX_LENGTH}
								</Hint>
							</div>
						</div>
					</div>
					<div className={styles.stopActions}>
						<div className={stopIndex === 0 ? styles.hiddenButton : undefined}>
							<Button
								data-testid={`stop-delete-${stopIndex}`}
								formAction={"none"}
								onClick={() => onDeleteStop(stopIndex)}
								svgIcon={trash_01}
								title={"Delete stop"}
								variant={"dangerTertiary"}
							/>
						</div>
						<Button
							data-testid={`stop-copy-${stopIndex}`}
							formAction={"none"}
							onClick={() => onCopyStop(stopIndex)}
							svgIcon={copy_02}
							title={"Duplicate stop"}
							variant={"tertiary"}
						/>
						<Button
							data-testid={`stop-drag-${stopIndex}`}
							formAction={"none"}
							svgIcon={dotsGrid}
							title={"Drag to reorder"}
							variant={"tertiary"}
						/>
					</div>
				</div>
			</div>
		</Card>
	);
}
